import {
  AorCancellationStatus,
  AutoPayStatus,
  BenefitStatus,
  CarrierRequestStatus,
  CoverageChange,
  EmployeePaymentMethodType,
  EmployeeRole,
  EmploymentStatus,
  EmploymentType,
  EnrollmentStatus,
  FamilyUnit,
  InitialPremiumPaymentStatus,
  MetalLevel,
  OnboardingType,
  PlanMarket,
  PolicyType,
  QualifyingLifeEventType,
  SelfEnrollType,
  SubmissionType,
  UserStatus,
} from '@zorro/clients';
import { getMetalLevelLabel } from '@zorro/common-utils';
import {
  PolicyTypeLabelConfig,
  aorCancellationStatusLabelConfig,
  carrierRequestStatusLabelConfig,
  coverageChangeLabelConfig,
  employeeRoleLabelConfig,
  employerFamilyUnitLabelConfig,
  employmentTypeLabelConfig,
  onboardingTypeLabelConfig,
  paymentMethodLabelConfig,
  qualifyingLifeEventLabelConfig,
  selfEnrollTypeLabelConfig,
  submissionTypeToStringMap,
} from '@zorro/types';
import { screamingSnakeCaseToText } from '@zorro/web/ui-utils';

import { agentBadgeConfig } from '../AgentStatusBadge';
import { getBenefitStatusBadgeView } from '../BenefitStatus';
import { benefitEligibilityLabelConfig } from '../EligibilityStatusBadge/EligibilityStatusBadgeUtils';
import { getEnrollmentStatusView } from '../EnrollmentStatus';
import { ENROLLMENT_TAG_ORDER, enrollmentTagConfig } from '../EnrollmentTags';
import { autoPayStatusToProps, initialPremiumStatusToProps } from '../Payments';

export const NULL_SELECT_OPTION = {
  value: 'null',
  label: '-',
};

export const YES_NO_SELECT_OPTIONS = [
  {
    value: 'true',
    label: 'Yes',
  },
  {
    value: 'false',
    label: 'No',
  },
];

export const EMPLOYEE_ROLE_SELECT_OPTIONS = Object.values(EmployeeRole)
  .filter((employeeRole) => employeeRole !== EmployeeRole.OTHER)
  .map((option) => ({
    value: String(option),
    label: employeeRoleLabelConfig[option],
  }));

export const EMPLOYMENT_TYPE_SELECT_OPTIONS = Object.values(EmploymentType).map(
  (option) => ({
    value: String(option),
    label: employmentTypeLabelConfig[option],
  })
);

export const ENROLLMENT_STATUS_SELECT_OPTIONS = Object.values(EnrollmentStatus)
  .filter(
    (enrollmentStatus) => enrollmentStatus !== EnrollmentStatus.ELECTION_ACTIVE
  )
  .map((option) => ({
    value: String(option),
    label: getEnrollmentStatusView(option).label,
  }));

export const PLAN_STATUS_SELECT_OPTIONS = Object.values(BenefitStatus)
  .filter((status) => getBenefitStatusBadgeView(status))
  .map((option) => ({
    value: String(option),
    label: getBenefitStatusBadgeView(option)?.label ?? String(option),
  }));

export const COVERAGE_CHANGE_SELECT_OPTIONS = Object.values(CoverageChange).map(
  (option) => ({
    value: String(option),
    label: coverageChangeLabelConfig[option],
  })
);

export const FAMILY_UNIT_SELECT_OPTIONS = Object.values(FamilyUnit).map(
  (option) => ({
    value: String(option),
    label: employerFamilyUnitLabelConfig[option],
  })
);

export const INITIAL_PREMIUM_STATUS_SELECT_OPTIONS = Object.values(
  InitialPremiumPaymentStatus
).map((option) => ({
  value: String(option),
  label: initialPremiumStatusToProps[option].label,
}));

export const AUTO_PAY_STATUS_SELECT_OPTIONS = Object.values(AutoPayStatus).map(
  (option) => ({
    value: String(option),
    label: autoPayStatusToProps[option].label,
  })
);

export const METAL_LEVEL_SELECT_OPTIONS = Object.values(MetalLevel).map(
  (option) => ({
    value: String(option),
    label: getMetalLevelLabel(option),
  })
);

export const PLAN_MARKET_SELECT_OPTIONS = Object.values(PlanMarket).map(
  (option) => ({
    value: String(option),
    label: screamingSnakeCaseToText(option),
  })
);

export const ENROLLMENT_TAGS_SELECT_OPTIONS = ENROLLMENT_TAG_ORDER.map(
  (option) => ({
    value: String(option),
    label: enrollmentTagConfig[option].label,
  })
);

export const SELF_ENROLL_SELECT_OPTIONS = Object.values(SelfEnrollType).map(
  (option) => ({
    value: String(option),
    label: selfEnrollTypeLabelConfig[option],
  })
);

export const PLAN_TYPE_SELECT_OPTIONS = Object.values(PolicyType).map(
  (option) => ({
    value: String(option),
    label: PolicyTypeLabelConfig[option],
  })
);

export const AOR_CANCELLATION_STATUS_SELECT_OPTIONS = Object.values(
  AorCancellationStatus
).map((option) => ({
  value: String(option),
  label: aorCancellationStatusLabelConfig[option],
}));

export const CARRIER_REQUEST_STATUS_SELECT_OPTIONS = Object.values(
  CarrierRequestStatus
).map((option) => ({
  value: String(option),
  label: carrierRequestStatusLabelConfig[option],
}));

export const USER_STATUS_SELECT_OPTIONS = Object.values(UserStatus).map(
  (option) => ({
    value: String(option),
    label: agentBadgeConfig[option].label,
  })
);

export const BENEFIT_ELIGIBILITY_SELECT_OPTIONS = Object.values(
  EmploymentStatus
).map((option) => ({
  value: String(option),
  label: benefitEligibilityLabelConfig[option],
}));

export const ONBOARDING_TYPE_SELECT_OPTIONS = Object.values(OnboardingType).map(
  (option) => ({
    value: String(option),
    label: onboardingTypeLabelConfig[option],
  })
);

export const SUBMISSION_TYPE_SELECT_OPTIONS = Object.values(SubmissionType).map(
  (option) => ({
    value: String(option),
    label: submissionTypeToStringMap[option],
  })
);

export const PAYMENT_METHOD_TYPE_SELECT_OPTIONS = Object.values(
  EmployeePaymentMethodType
).map((option) => ({
  value: String(option),
  label: paymentMethodLabelConfig[option],
}));

export const QLE_TYPE_SELECT_OPTIONS = Object.values(
  QualifyingLifeEventType
).map((option) => ({
  value: String(option),
  label: qualifyingLifeEventLabelConfig[option],
}));
